import { championIconUrl } from "./championStats";
import { IBanList, IGameRecord, IMatchGamePlayer } from "../types/types";

export const DDRAGON_VERSION = "16.13.1";

/** Riot sentinel for a ban slot where no champion was banned. */
export const NO_BAN_CHAMPION_ID = -1;

export const isNoBanChampionId = (championId: number): boolean => championId === NO_BAN_CHAMPION_ID;

export const isFilledBanChampionId = (championId: number): boolean =>
  championId > 0 || isNoBanChampionId(championId);

export const championIconById = (championId: number): string =>
  `https://raw.communitydragon.org/latest/plugins/rcp-be-lol-game-data/global/default/v1/champion-icons/${String(championId)}.png`;

export const noBanIconUrl = (): string => championIconById(NO_BAN_CHAMPION_ID);

export const championIconForPlayer = (player: IMatchGamePlayer): string => {
  if (player.champion_name.trim()) {
    return championIconUrl(player.champion_name);
  }
  if (player.champion_id > 0) {
    return championIconById(player.champion_id);
  }
  return championIconUrl("Unknown");
};

export const championIconForBan = (championId: number, championName?: string): string => {
  if (isNoBanChampionId(championId)) {
    return noBanIconUrl();
  }
  if (championName?.trim()) {
    return championIconUrl(championName);
  }
  if (championId > 0) {
    return championIconById(championId);
  }
  return championIconUrl("Unknown");
};

export const itemIconUrl = (itemId: number): string | null => {
  if (!itemId) return null;
  return `https://ddragon.leagueoflegends.com/cdn/${DDRAGON_VERSION}/img/item/${String(itemId)}.png`;
};

const SCOREBOARD_ICON_BASE = "https://raw.communitydragon.org/latest/game/assets/ux/scoreboard";
const MINIMAP_ICON_BASE = "https://raw.communitydragon.org/latest/game/assets/ux/minimap/icons";

const DRAGON_ICON_FILE: Record<string, string> = {
  FIRE_DRAGON: "_infernaldrake.png",
  WATER_DRAGON: "_oceandrake.png",
  EARTH_DRAGON: "_mountaindrake.png",
  AIR_DRAGON: "_clouddrake.png",
  CHEMTECH_DRAGON: "_chemtechdrake.png",
  HEXTECH_DRAGON: "_hextechdrake.png",
  ELDER_DRAGON: "_elderdrake.png",
};

export type ObjectiveIconKind = "turret" | "baron" | "grub" | "herald" | "elder";

export const objectiveIconUrl = (kind: ObjectiveIconKind): string => {
  switch (kind) {
    case "turret":
      return `${MINIMAP_ICON_BASE}/tower.png`;
    case "baron":
      return `${SCOREBOARD_ICON_BASE}/_baronnashor.png`;
    case "grub":
      return `${MINIMAP_ICON_BASE}/grub.png`;
    case "herald":
      return `${SCOREBOARD_ICON_BASE}/_riftherald.png`;
    case "elder":
      return `${SCOREBOARD_ICON_BASE}/_elderdrake.png`;
  }
};

export const genericDragonIconUrl = (): string => `${SCOREBOARD_ICON_BASE}/_dragon.png`;

export const DRAGON_TYPE_OPTIONS = [
  "FIRE_DRAGON",
  "WATER_DRAGON",
  "EARTH_DRAGON",
  "AIR_DRAGON",
  "CHEMTECH_DRAGON",
  "HEXTECH_DRAGON",
  "ELDER_DRAGON",
] as const;

export type DragonType = (typeof DRAGON_TYPE_OPTIONS)[number];

const DRAGON_LABELS: Record<string, string> = {
  FIRE_DRAGON: "Infernal Drake",
  WATER_DRAGON: "Ocean Drake",
  EARTH_DRAGON: "Mountain Drake",
  AIR_DRAGON: "Cloud Drake",
  CHEMTECH_DRAGON: "Chemtech Drake",
  HEXTECH_DRAGON: "Hextech Drake",
  ELDER_DRAGON: "Elder Dragon",
};

export const dragonLabel = (subtype: string): string => {
  const key = subtype.trim().toUpperCase();
  if (DRAGON_LABELS[key]) return DRAGON_LABELS[key];
  return key
    .replace(/_DRAGON$/i, "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const dragonIconUrl = (subtype: string): string => {
  const file = DRAGON_ICON_FILE[subtype.trim().toUpperCase()];
  if (!file) return genericDragonIconUrl();
  return `${SCOREBOARD_ICON_BASE}/${file}`;
};

const readString = (value: unknown): string => (typeof value === "string" ? value : "");

export const normalizeBan = (raw: unknown): IBanList | null => {
  if (!raw || typeof raw !== "object") return null;
  const ban = raw as Record<string, unknown>;
  const championId = Number(
    ban.championId ?? ban.champion_id ?? ban.ChampionID ?? ban.championID ?? 0,
  );
  const pickTurn = Number(ban.pickTurn ?? ban.pick_turn ?? ban.PickTurn ?? 0);
  if (!Number.isFinite(championId) || championId === 0) return null;
  return { championId, pickTurn };
};

export const normalizeBanList = (raw: unknown): IBanList[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (typeof entry === "string") {
        try {
          return normalizeBan(JSON.parse(entry) as unknown);
        } catch {
          return null;
        }
      }
      return normalizeBan(entry);
    })
    .filter((ban): ban is IBanList => ban !== null)
    .sort((a, b) => a.pickTurn - b.pickTurn);
};

export const normalizeGamePlayer = (raw: Record<string, unknown>): IMatchGamePlayer => ({
  team_id: Number(raw.team_id ?? raw.teamId ?? 0),
  puuid: readString(raw.puuid),
  champion_id: Number(raw.champion_id ?? raw.championId ?? 0),
  champion_name: readString(raw.champion_name ?? raw.championName),
  champions_killed: Number(raw.champions_killed ?? raw.championsKilled ?? 0),
  deaths: Number(raw.deaths ?? 0),
  assists: Number(raw.assists ?? 0),
  role: readString(raw.role ?? raw.team_position ?? raw.teamPosition ?? raw.lane),
  gold_earned: Number(raw.gold_earned ?? raw.goldEarned ?? 0),
  level: Number(raw.level ?? 0),
  damage: Number(raw.damage ?? 0),
  vision_score: Number(raw.vision_score ?? raw.visionScore ?? 0),
  wards_placed: Number(raw.wards_placed ?? raw.wardsPlaced ?? 0),
  wards_killed: Number(raw.wards_killed ?? raw.wardsKilled ?? 0),
  creep_score: Number(raw.creep_score ?? raw.creepScore ?? 0),
  items: Array.isArray(raw.items) ? raw.items.map((item) => Number(item)) : [],
  cs_at_15: Number(raw.cs_at_15 ?? raw.csAt15 ?? 0),
  double_kills: Number(raw.double_kills ?? raw.doubleKills ?? 0),
  triple_kills: Number(raw.triple_kills ?? raw.tripleKills ?? 0),
  quadra_kills: Number(raw.quadra_kills ?? raw.quadraKills ?? 0),
  penta_kills: Number(raw.penta_kills ?? raw.pentaKills ?? 0),
  summoner1_id: Number(raw.summoner1_id ?? raw.summoner1Id ?? 0),
  summoner2_id: Number(raw.summoner2_id ?? raw.summoner2Id ?? 0),
});

export const normalizeGameRecord = (game: IGameRecord): IGameRecord => ({
  ...game,
  players: Array.isArray(game.players)
    ? game.players.map((player) =>
        normalizeGamePlayer(player as unknown as Record<string, unknown>),
      )
    : [],
  blue_bans: normalizeBanList(game.blue_bans),
  red_bans: normalizeBanList(game.red_bans),
  blue_dragon_types: Array.isArray(game.blue_dragon_types) ? game.blue_dragon_types : [],
  red_dragon_types: Array.isArray(game.red_dragon_types) ? game.red_dragon_types : [],
});
